// SectionCItemsTable.js - simple informational rows for Section C (no cost columns,
// no merge groups - just Unit Name / Qty / Details, since Section C is excluded from totals).
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import ImageUploadCell from './ImageUploadCell';
import { createSectionCItem } from './interiorQuotationModel';

const fieldClass =
  'h-10 w-full rounded-lg border border-amber-200 bg-white px-2.5 text-sm text-gray-800 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30';

const iconBtnClass =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-200 bg-white text-amber-600 transition-colors hover:border-amber-400 hover:text-amber-800 disabled:cursor-not-allowed disabled:opacity-40';

const SectionCItemsTable = ({ items, onItemsChange, siteId = null }) => {
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);

  const updateItem = (index, patch) => {
    const next = [...items];
    next[index] = { ...next[index], ...patch };
    onItemsChange(next);
  };

  const moveItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const arr = [...items];
    [arr[index], arr[target]] = [arr[target], arr[index]];
    onItemsChange(arr);
  };

  const addItemAfter = (index) => {
    const next = [...items];
    next.splice(index + 1, 0, createSectionCItem());
    onItemsChange(next);
  };

  const pendingItem =
    pendingRemoveIndex != null ? items[pendingRemoveIndex] : null;

  return (
    <div className="overflow-x-auto rounded-xl border border-amber-100">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-amber-50 text-amber-800">
          <tr>
            <th className="px-2 py-2 text-left font-semibold">#</th>
            <th className="min-w-[200px] px-2 py-2 text-left font-semibold">
              Unit Name <span className="text-red-500">*</span>
            </th>
            <th className="min-w-[100px] px-2 py-2 text-left font-semibold">Qty</th>
            <th className="min-w-[260px] px-2 py-2 text-left font-semibold">Notes</th>
            <th className="min-w-[150px] px-2 py-2 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.id} className="border-t border-amber-100 align-top">
              <td className="px-2 py-2 text-gray-500">{index + 1}</td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  value={item.unitName}
                  onChange={(e) => updateItem(index, { unitName: e.target.value })}
                  placeholder="e.g. Chimney"
                  className={fieldClass}
                />
                <ImageUploadCell
                  image={item.image}
                  siteId={siteId}
                  onChange={(image) => updateItem(index, { image })}
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.qty}
                  onChange={(e) => updateItem(index, { qty: e.target.value })}
                  placeholder="0"
                  className={fieldClass}
                />
              </td>
              <td className="px-2 py-2">
                <textarea
                  value={item.details}
                  onChange={(e) => updateItem(index, { details: e.target.value })}
                  placeholder="Notes - to be paid to local vendor on actuals..."
                  rows={2}
                  className="w-full resize-y rounded-lg border border-amber-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                />
              </td>
              <td className="px-2 py-2">
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    title="Move up"
                    className={iconBtnClass}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(index, 1)}
                    disabled={index === items.length - 1}
                    title="Move down"
                    className={iconBtnClass}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => addItemAfter(index)}
                    title="Add row below"
                    className={iconBtnClass}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingRemoveIndex(index)}
                    disabled={items.length === 1}
                    title="Remove row"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-amber-200 bg-white text-red-500 hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmDialog
        isOpen={pendingRemoveIndex != null}
        title="Remove row?"
        message={`This will permanently remove "${
          pendingItem?.unitName || 'this row'
        }". This cannot be undone.`}
        confirmLabel="Remove"
        onCancel={() => setPendingRemoveIndex(null)}
        onConfirm={() => {
          const index = pendingRemoveIndex;
          setPendingRemoveIndex(null);
          if (index != null) onItemsChange(items.filter((_, i) => i !== index));
        }}
      />
    </div>
  );
};

export default SectionCItemsTable;
